import classNames from "classnames";
import { Dialog, ValidationError } from "client/Components";
import { EMPTY_ARRAY, isNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef
} from "react";
import styles from "./UnmanagedComposerDialog.module.less";
import { CommunityPostComposerFooter } from "./CommunityPostComposerFooter";
import { RichTextEditorState } from "client/Components/RichTextEditor/types";
import { IValueInputAttachable } from "client/Forms/attachable";
import { CommunityPostComposerMessage, CommunityPostComposerMessageHandle } from "./CommunityPostComposerMessage";
import { PostComposerFormData } from "./common";
import { CommunityViewModel } from "client/Api/Communities/communityPosts";
import { PostComposerOptions } from "./NewPostComposer";
import { Separator } from "./Separator";
import { PollEditor } from "./PollEditor";
import { InputValidationResultKind } from "client/Validation";
import { CommunityPostImageViewModel, CommunityPostPollViewModel } from "client/Api/Communities/communityPostViewer";
import { CommunityPostComposerImages } from "./CommunityPostComposerImages";
import { AlertManager, useAlerts } from "client/Hooks";
import { useUploadCommunityPostImage } from "./useUploadCommunityPostImage";
import { CommunityPostComposerLinkedDocuments } from "./CommunityPostComposerLinkedDocuments";
import { LinkedDocumentFormData } from "../../common";

export type UnmanagedComposerDialogProps = {
	attachable: IValueInputAttachable<PostComposerFormData>;
	options: PostComposerOptions;
	isMobile: boolean;
	isBusy: boolean;
	isTouch: boolean;
	onIsBusyChange: (isBusy: boolean) => void;
	onClose: () => void;
	onChangeEditorState: (newValue: RichTextEditorState, isSignificant: boolean) => void;
	onBlurEditor: (newValue: RichTextEditorState) => void;
	editorState: RichTextEditorState;
	onSubmit: () => void;
	shouldShowMessageToolbar: boolean;
	onShouldShowMessageToolbarChange: (value: boolean) => void;
	isSharepointEnabled: boolean;
	isWebDavAvailable: boolean;
	canAddLinkedDocuments: boolean;
	onAddLinkedDocument: (() => Promise<void>) | null;
	onOpenLinkedDocument: (document: LinkedDocumentFormData) => void;
} & (
	{
		mode: "create";
		communities: readonly CommunityViewModel[];
		initialCommunityId: number | null;
	}
	| {
		mode: "edit";
		community: CommunityViewModel;
		initialImages: readonly CommunityPostImageViewModel[];
		initialPoll: CommunityPostPollViewModel | null;
	}
);

export const UnmanagedComposerDialog = (props: UnmanagedComposerDialogProps): ReactElement => {
	const {
		attachable,
		options,
		isMobile,
		isBusy,
		isTouch,
		onIsBusyChange,
		mode,
		onClose,
		onChangeEditorState,
		onBlurEditor,
		editorState,
		onSubmit,
		shouldShowMessageToolbar,
		onShouldShowMessageToolbarChange,
		isSharepointEnabled,
		isWebDavAvailable,
		canAddLinkedDocuments,
		onAddLinkedDocument,
		onOpenLinkedDocument
	} = props;

	const alertManager: AlertManager = useAlerts();
	const messageRef: RefObject<CommunityPostComposerMessageHandle | null> = useRef<CommunityPostComposerMessageHandle>(null);

	const {
		onSelectImages,
		dialogElement: imageDialog
	} = useUploadCommunityPostImage(
		attachable,
		onIsBusyChange,
		options,
		alertManager
	);

	const focusEditorHandler = useCallback(
		(): void => {
			messageRef.current
				?.focus();
		},
		[]
	);

	useEffect(
		(): void => focusEditorHandler(),
		[focusEditorHandler]
	);

	const renderMessageToolbar = (): ReactElement | null => {
		if(!shouldShowMessageToolbar) {
			return null;
		}

		return (
			<CommunityPostComposerFooter.Toolbar
				onChangeEditorState={onChangeEditorState}
				editorState={editorState}
				onFocusEditor={focusEditorHandler}
			/>
		);
	};

	const communities = useMemo(
		(): readonly CommunityViewModel[] => {
			return mode === "create"
				? props.communities
				: [props.community];
		},
		[mode, props]
	);

	const initialCommunityId = useMemo(
		(): number | null => {
			if (mode === "create") {
				return props.initialCommunityId;
			}

			return props.community
				.id;
		},
		[mode, props]
	);

	const initialPoll = useMemo(
		(): CommunityPostPollViewModel | null => {
			if (mode === "create") {
				return null;
			}

			return props.initialPoll;
		},
		[mode, props]
	);

	const { value } = attachable;
	const hasPoll: boolean = !value.poll.isSet && isNotNil(initialPoll)
		|| value.poll.isSet && isNotNil(value.poll.value);

	const isValid: boolean = attachable
		.validation
		.getValidationState()
		.kind === InputValidationResultKind.Valid;

	const footer: ReactElement = (
		<CommunityPostComposerFooter
			attachable={attachable}
			onSubmit={onSubmit}
			onSelectImages={onSelectImages}
			communities={communities}
			initialCommunityId={initialCommunityId}
			isBusy={isBusy}
			hasPoll={hasPoll}
			toolbar={renderMessageToolbar()}
			canAddLinkedDocuments={canAddLinkedDocuments}
			onAddLinkedDocument={onAddLinkedDocument}
		/>
	);

	return (
		<>
			<Dialog
				size={isMobile ? "large" : "long"}
				hasBusyIndicator={isBusy}
				height="fill"
				trackingId={
					mode === "edit"
						? "edit-community-post--dialog"
						: "create-community-post-dialog"
				}
			>
				<Dialog.Header
					title={localize(
						mode === "edit"
							? "_EditPost"
							: "_NewPost"
					)}
					onClose={onClose}
					hasBorder={isMobile}
				/>
				<Dialog.Body>
					<div
						className={classNames(
							styles.container,
							{
								[styles.container_hasError]: !isValid,
								[styles.container_fullWidth]: isMobile
							})
						}
					>
						<div className={styles.content}>
							<div className={styles.messageContainer}>
								<CommunityPostComposerMessage
									ref={messageRef}
									value={editorState}
									onChange={onChangeEditorState}
									onBlur={onBlurEditor}
									onFocus={() => onShouldShowMessageToolbarChange(true)}
								/>
							</div>
							<div className={styles.pollAndImages}>
								{!isMobile && (
									<ValidationError {...attachable.for("message").attachToValidationError()} />
								)}
								{hasPoll && (
									<>
										<Separator />
										<PollEditor
											attachable={attachable.for("poll")}
											onFocus={() => onShouldShowMessageToolbarChange(false)}
											initialPoll={initialPoll}
										/>
									</>
								)}
								{attachable.for("images")
									.when(
										x => x.length > 0,
										handler => (
											<>
												<Separator />
												<CommunityPostComposerImages
													attachable={handler}
													initialImages={
														mode === "edit"
															? props.initialImages
															: EMPTY_ARRAY
													}
													areImagesScrollable={isMobile}
												/>
											</>
										)
									)
								}
								{attachable.for("linkedDocuments")
									.when(
										x => x.length > 0,
										handler => (
											<>
												<Separator />
												<CommunityPostComposerLinkedDocuments
													attachable={handler}
													isSharepointEnabled={isSharepointEnabled}
													isTouch={isTouch}
													isMobile={isMobile}
													isWebDavAvailable={isWebDavAvailable}
													onOpenDocument={onOpenLinkedDocument}
												/>
											</>
										)
									)
								}
							</div>
						</div>
						{!isMobile && (
							<div className={styles.footer}>
								{footer}
							</div>
						)}
					</div>
				</Dialog.Body>
				<Dialog.Footer>
					{isMobile && (
						<>
							{footer}
						</>
					)}
				</Dialog.Footer>
			</Dialog>
			{imageDialog}
		</>
	);
};
UnmanagedComposerDialog.displayName = "UnmanagedComposerDialog";
